import React, { Component } from 'react';
import moment from 'moment';
import { withRouter } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { connect } from 'react-redux'

// components
import { DirectoryFooter, DirectorySlidingContent, DirTable, InvestorDetailRow, InvestorDetailModal } from '/src/pages/directories/components';

// helpers
import * as startupHelper from '/src/pages/directories/helpers/startupDirHelpers';
import { amountFormatter } from "/src/lib/helpers";
import { createColumnSortHandlers, resolveColumnSortKey, resolveViewOrder } from '/src/pages/directories/helpers/columnSortHelpers';
import { createRequestGuard } from '/src/pages/directories/helpers/requestGuard';

// constants
import { RECORD_COUNT_DEFAULT } from '/src/lib/constants';
import { MAGNITT_ACCESS_ROLES } from '/src/lib/constants/userRoles';
import { startupExitsFilterConfig } from '/src/pages/directories/constants/filterConfigStartup';
import { testDataExits } from '/src/components/LockedContent';
import { EXITS_TABLE_NAME_SORT_KEY, EXITS_TABLE_HEADERS } from '/src/pages/directories/constants/exitsTableConfig';

// apis
import exitApi from '/src/apis/exitApi';

// client directory mixes startups/corporates, so "Startup HQ" (shared with Startup.js) is relabeled "HQ" here
export const resolveExitsFieldName = (field, isClientDirectory) =>
    (isClientDirectory && field.keyName === 'location_data') ? 'HQ' : field.fieldName;

const DEFAULT_EXITS_ORDER = { field: 'date_raised', order: 'desc' };

export class StartupDirectoryExits extends Component {

    constructor() {
        super();
        this.entityType = 'EXITS VIEW';
        this.state = {
            count: 0,
            limit: RECORD_COUNT_DEFAULT.default,
            page: 1,
            data: false,
            loadingDirPage: false,
            selectedFundingStageName: '',
            defaultFilters: {},
            //table headers
            tableHeaders: EXITS_TABLE_HEADERS,
            removeSkeletonTable: false
        };
        this.isComponentMounted = false;
        // guards against a slower, older filterValues response overwriting a newer one
        // (e.g. clicking sort/a filter repeatedly before the first request returns)
        this.listGuard = createRequestGuard();
    }

    componentDidMount() {
        this.isComponentMounted = true;
    }

    componentDidUpdate(prevProps) {
        const { tempFilter } = this.props;

        if (prevProps.exitsData.refresh !== this.props.exitsData.refresh) {
            // A sort-only change never affects which records match the filters, so the locked-user
            // count fallback below doesn't need to be refetched just because the table was re-sorted.
            const { order: prevOrder, ...prevFilterRest } = prevProps.exitsData.filter || {};
            const { order: nextOrder, ...nextFilterRest } = this.props.exitsData.filter || {};
            const sortOnlyChanged = isEqual(prevFilterRest, nextFilterRest) && !isEqual(prevOrder, nextOrder);
            this.filterValues(tempFilter, sortOnlyChanged);
        }
    }

    componentWillUnmount() {
        this.isComponentMounted = false;
        this.listGuard.cancel();
    }

    updateState = (state, callback) => {
        if (this.isComponentMounted) {
            this.setState(state, callback);
        }
    }

    // Valid fields = column header sort keys plus this tab's "Sort by" presets (exitsData.sortList).
    // Some presets (e.g. "Total Funding") have no column, so headers alone would reject them.
    getValidSortFields = () => [
        EXITS_TABLE_NAME_SORT_KEY,
        ...EXITS_TABLE_HEADERS.map(h => resolveColumnSortKey(h, this.props.sortKeyOverrides)),
        ...(this.props.exitsData.sortList || []).map(item => item.field)
    ];

    sortHandlers = createColumnSortHandlers(
        () => resolveViewOrder(this.props.exitsData.order, this.props.tempFilter?.order, this.getValidSortFields(), DEFAULT_EXITS_ORDER),
        (order) => this.props.changeOrder(order),
        { isEnabled: () => this.props.enableColumnSort, getSortList: () => this.props.exitsData.sortList }
    );

    handleColumnSort = this.sortHandlers.handleColumnSort;
    renderSortHeader = this.sortHandlers.renderSortHeader;

    filterValues = (filter = "", sortOnlyChanged = false) => {

        // only start a request when fetching, else it cancels an in-flight one with nothing to replace it
        const hasDirectoryAccess = this.props.user.roles.includes(MAGNITT_ACCESS_ROLES.directory_access);
        const { id: requestId, signal } = (hasDirectoryAccess || !sortOnlyChanged) ? this.listGuard.start() : {};

        const { isPageChanged } = filter
        delete filter.isPageChanged;

        let { limit, page } = this.state;
        let {
            totalAmountMinSuffix, totalAmountMaxSuffix, amountraisedMinSuffix, amountraisedMaxSuffix, exitsData,
            filterConfig = startupExitsFilterConfig,
            prepFilters = startupHelper.prepNewFiltersFunding,
        } = this.props

        const tempFilter = { ...filter, ...this.state.defaultFilters };

        this.props.setState({
            exitsViewFilters: {
                defaultFilters: this.state.defaultFilters,
                selectedFilters: tempFilter
            }
        });

        // Prefer this tab's own remembered order; Startup.js doesn't track it yet, so
        // fall back to tempFilter.order only if it's actually a field this tab can sort by.
        const activeOrder = resolveViewOrder(exitsData.order, this.props.tempFilter?.order, this.getValidSortFields(), DEFAULT_EXITS_ORDER);
        const sort = `${activeOrder.field}^:${activeOrder.order}`;
        const { finalFilters } = prepFilters(tempFilter, cloneDeep(filterConfig), totalAmountMinSuffix, totalAmountMaxSuffix, amountraisedMinSuffix, amountraisedMaxSuffix);
        const newFilters = finalFilters;

        // If page hasn't changed, set it to 1
        // This will help when page was set to > 1 and filters were applied
        if (!isPageChanged) {
            page = 1;
            this.updateState({ page })
        }

        if (hasDirectoryAccess) {
            // Add default fields to exits view
            let extraFields = [];
            this.state.tableHeaders.map((field) => {
                extraFields.push(field.keyName);
            })

            this.updateState({ loadingDirPage: true });
            exitApi.getExitData(extraFields, newFilters, limit, page, sort, this.props.entityType, signal).then(response => {
                if (this.listGuard.isStale(requestId)) return;

                if (!isEmpty(response)) {
                    let exits = response.exits;
                    this.updateState({
                        data: exits,
                        count: response.totalRecords,
                        loadingDirPage: false
                    });
                    this.props.setState({ exitsCount: response.totalRecords })
                } else {
                    this.updateState({
                        data: [],
                        count: 0,
                        loadingDirPage: false
                    });
                    this.props.setState({ exitsCount: 0 })
                }
            }).catch(err => {
                if (this.listGuard.isStale(requestId)) return;
                console.log("Error:", err)
                this.updateState({ data: [], count: 0, loadingDirPage: false });
            });
        } else if (!sortOnlyChanged) {
            // locked users only ever see this count, never the sorted rows themselves, so a
            // sort-only change (no filter change) has nothing new to fetch here.
            exitApi.getExitDataCount(newFilters, this.props.entityType, signal).then(response => {
                if (this.listGuard.isStale(requestId)) return;

                if (!isEmpty(response)) {
                    this.updateState({
                        count: response.totalRecords
                    });
                    this.props.setState({ exitsCount: response.totalRecords })
                } else {
                    this.updateState({
                        count: 0,
                    });
                    this.props.setState({ exitsCount: 0 })
                }
            }).catch(err => {
                if (this.listGuard.isStale(requestId)) return;
                console.log("Error:", err)
            });
        }
    }

    //format entity data for directory slider
    handleEntityData = () => {
        let entityData = null
        if (this.state.data && this.state.data.length > 0) {
            entityData = this.state.data.map((obj) => {
                let investedInData = null

                if (obj.investedin_data) {
                    investedInData = obj.investedin_data
                    investedInData.logo_data = { path: investedInData.logo || null }
                    investedInData.primary_industry_data = obj.primary_industry_data && obj.primary_industry_data.length > 0 ?
                        obj.primary_industry_data[0] : null
                    investedInData.hq_data = obj.investedin_hq_data || null
                }

                return investedInData
            })
        }

        return entityData
    }

    render() {

        const { selectedInvestorModal } = this.state;

        let content = null;
        let tableContent = {};

        tableContent.rows = [];
        tableContent.pinnedRow = [];
        const tableContainerName = "dir-exits";

        const { user, isExitsView, isClientDirectory } = this.props;
        let userHasDirectoryAccess = false;
        if (user && user.roles && user.roles.length > 0) {
            userHasDirectoryAccess = (user.roles.includes(MAGNITT_ACCESS_ROLES.directory_access) || user.roles.includes(MAGNITT_ACCESS_ROLES.limited_directory_access))
        }

        const showTestData = (!user.is_loggedin || !userHasDirectoryAccess)

        let directoryData = null;

        if (showTestData) {
            directoryData = testDataExits;
        } else if (this.state.data !== false && this.state.data.length) {
            directoryData = this.state.data;
        }

        if (directoryData) {
            content = directoryData.map((exit, i) => {

                if (exit.investedin_data != null) {

                    let investedInName = "";
                    let investedinLogo = "";
                    let investedinType = "";
                    let investedinId = exit.investedin_id;

                    if (!isEmpty(exit.investedin_data)) {
                        investedInName = exit.investedin_data.name;
                        investedinLogo = exit.investedin_data.logo;
                        investedinType = exit.investedin_data.type;
                    }

                    let image;
                    if (showTestData) {
                        image = <div className='startupLogo'></div>
                    } else {
                        if (investedinLogo) {
                            image = investedinLogo;
                            image = <div className='startupLogo' style={{ backgroundImage: `url('${image}')` }}></div>
                        } else {
                            image = <div className='startupLogo'><h2>{investedInName.substr(0, 1).toUpperCase()}</h2></div>
                        }
                    }

                    let nameRow = (
                        <a className="d-flex align-items-center">
                            {image}
                            <div className="flex-column" style={{ marginLeft: 10, marginTop: -4 }}>
                                <div className="startup-name" data-action="expand-slider" data-entityid={investedinId}>
                                    {investedInName}
                                </div>
                            </div>
                        </a>
                    );

                    // send to table
                    tableContent.header = [this.renderSortHeader('Name', EXITS_TABLE_NAME_SORT_KEY)];
                    let fieldData = [{ name: nameRow }];

                    // check default fields
                    this.state.tableHeaders.map((field) => {

                        let itemField = exit[field.keyName];
                        let tempData = "-";

                        tableContent.header.push(
                            this.renderSortHeader(resolveExitsFieldName(field, isClientDirectory), resolveColumnSortKey(field, this.props.sortKeyOverrides))
                        );

                        if (field.keyName === 'amount' && !itemField) {
                            itemField = 0;
                        }

                        if (itemField || itemField == 0) {
                            switch (field.keyName) {
                            case "date_raised":
                                if (itemField) {
                                    tempData = moment(itemField).format("MMM YYYY");
                                }
                                break;
                            case "primary_industry_data":
                                if (itemField && itemField.length > 0) {
                                    let arrTemp = [];
                                    itemField.forEach(ind => {
                                        if (ind && ind.name) {
                                            arrTemp.push(ind.name);
                                        }
                                    })
                                    tempData = arrTemp.length > 0 ? arrTemp.join(", ") : "-";
                                }
                                break;
                            case "location_data":
                            case "funding_stage_data":
                                tempData = itemField.name;
                                break;
                            case "acquirer_hq_data":
                                if (itemField[0] && itemField[0].name) {
                                    tempData = itemField[0].name;
                                }
                                break;
                            case "time_to_exit":
                            case "investedin_funding_count":
                            case "acquirer_type":
                                if (itemField) {
                                    tempData = itemField
                                }
                                break;
                            case "amount":
                                if (itemField > 0) {
                                    tempData = parseInt(itemField, 10);
                                    tempData = amountFormatter(tempData);
                                } else {
                                    // handle undisclosed and negative values (test data)
                                    tempData = "Undisclosed";
                                }
                                break;
                            case "total_amount_raised_data": {
                                const fundRaisedData = exit.total_amount_raised_data || {};
                                const totalAmount = fundRaisedData.amount || 0;
                                const hasProxy = Number(fundRaisedData.has_proxy) > 0;
                                if (totalAmount > 0) {
                                    const formatted = amountFormatter(totalAmount);
                                    tempData = <span className={hasProxy ? 'amount--estimated' : ''} title={hasProxy ? 'Estimated amount' : undefined}>{formatted}</span>;
                                } else {
                                    tempData = "Undisclosed";
                                }
                                break;
                            }
                            case "investors_data":
                                if (!isEmpty(itemField)) {
                                    tempData =
                                            <InvestorDetailRow
                                                investedInName={investedInName}
                                                investedInId={investedinId}
                                                investedInType={investedinType}
                                                investorData={itemField}
                                                setState={(s, c) => { this.updateState(s, c) }}
                                                userHasDirectoryAccess={userHasDirectoryAccess}
                                                selectedFundingStageName={false}
                                                isExitsView={true}
                                                isNestedModal={true} // to handle modal-backdrop behavior for nested modals i.e. modals that are not a direct child of body
                                            />;
                                }
                                break;

                            default:
                                tempData = "-";
                            }
                        }

                        fieldData.push({ [field.keyName]: tempData });

                    })

                    tableContent.rows.push(fieldData);
                }
            })

            const fullyLocked = (!user.is_loggedin || !userHasDirectoryAccess); // only sample data displayed
            content = (
                <DirTable content={tableContent}
                    user={this.props.user}
                    entityType={this.entityType}
                    fullyLocked={fullyLocked}
                    setState={(s, c) => { this.updateState(s, c) }}
                    isExitsView={true}
                />
            );

        }

        return (
            <div className={tableContainerName} >

                {content}
                <DirectoryFooter
                    // data
                    count={this.state.count}
                    data={this.state.data}
                    loadingDirPage={this.state.loadingDirPage}
                    allowNavigateWhileLoading
                    showTestData={showTestData}
                    user={this.props.user}
                    page={this.state.page}
                    limit={this.state.limit}
                    tempFilter={this.props.tempFilter}
                    // func
                    clearFilter={this.props.clearFilter}
                    filterValues={this.filterValues}
                    setState={(s, c) => { this.updateState(s, c) }}
                    removeSkeletonTable={this.state.removeSkeletonTable}
                    tabIsVisible={isExitsView}
                />

                {/* only display on exits view to prevent conflicts with startup table slider */}
                {(isExitsView === true) &&
                    <DirectorySlidingContent
                        // data
                        entityData={this.handleEntityData()}
                        entityType={this.props.entityType}
                        user={this.props.user}
                        isExitsView={isExitsView}
                        // functions
                        setState={(s, c) => { this.updateState(s, c) }}
                        tableContainerName={tableContainerName}
                    />
                }

                {/* note: checking isExitsView to avoid conflicts between the 2 InvestorDetailModals */}
                {isExitsView &&
                    <InvestorDetailModal
                        investedInName={this.state.investedInName}
                        investedInId={this.state.investedInId}
                        investorData={selectedInvestorModal}
                        userHasDirectoryAccess={userHasDirectoryAccess}
                        selectedFundingStageName={this.state.selectedFundingStageName}
                        isFundingView={false}
                    />
                }
            </div>
        )

    }
}

const mapStateToProps = (state) => ({
    user: state.userReducer
})

export default connect(mapStateToProps)(withRouter(StartupDirectoryExits))
